import React, { useRef, useState, useEffect } from 'react';

import { observer } from 'mobx-react';
import { View, AppState, Alert, Linking, StatusBar } from 'react-native';
import NetInfo from '@react-native-community/netinfo';
import * as SplashScreen from 'expo-splash-screen';
import * as ScreenOrientation from 'expo-screen-orientation';

ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.LANDSCAPE);
SplashScreen.preventAutoHideAsync();

import i18next from 'i18next';
import moment from 'moment';
import 'moment/locale/ru';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/pt';
import 'moment/locale/zh-cn';
import 'moment/locale/ja';
import 'moment/locale/ko';
import EventEmitter from 'events';
import * as Notifications from 'expo-notifications';

import WarriorInfoModal from './common/WarriorInfoModal';
import warriorInfoModalStore from './common/WarriorInfoModal/store';

import Message from './shared/Message';
import Overlay from './shared/Overlay';

import authStore from '../stores/auth';
import appStore from '../stores/app';
import heroStore from '../stores/hero';

import { SAFE_OFFSET, BACKGROUND_COLOR, PADDING, WINDOW_HEIGHT } from './styleConstants';

import { t, getFromStorage } from '../utils/appUtils';

import { IS_IOS, IS_WEB, EVENT_CLOSE_MODAL, LANGUAGE_CODE } from '../lib/appConstants';

import { initDb } from '../lib/dbCalls';

import Image from '../components/shared/Image';

import { FACEBOOK, APP_STORE_LINK, PLAY_STORE_LINK } from '../lib/config';
import Stack from './navigation';
import { useFonts } from 'expo-font';

declare const FB: any;

if (IS_WEB) {
  (window as any).fbAsyncInit = function () {
    FB.init(FACEBOOK);

    FB.AppEvents.logPageView();
  };

  (function (d: any, s: any, id: any) {
    const fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) {
      return;
    }
    const js = d.createElement(s);
    js.id = id;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';

    fjs.parentNode.insertBefore(js, fjs);
  })(document, 'script', 'facebook-jssdk');

  window.history.pushState(null, '', window.location.href);
  window.onpopstate = () => {
    window.history.go(authStore.isLoggedIn ? 1 : -1);
  };

  window.onerror = (
    errorMessage: any,
    url: any,
    lineNumber: any,
    columnNumber: any,
    errorObject: any,
  ) => {
    let exceptionDescription = errorMessage;
    if (errorObject !== undefined && errorObject.message !== undefined) {
      exceptionDescription = errorObject.message;
    }

    exceptionDescription += ` @ ${url}: ${lineNumber}: ${columnNumber}`;

    (window as any).ga('send', 'exception', {
      exDescription: exceptionDescription,
      exFatal: false,
    });
    return false;
  };
}

if (__DEV__) {
  require('../lib/debugConfigs');
}

const onConnectivityChange = () => {
  // TODO: Internet connection issue when came from background
  setTimeout(async () => {
    const { isConnected } = await NetInfo.fetch();
    appStore.toggleConnected(isConnected as boolean);
  }, 2000);
};

export const signOut = async () => {
  (global as any).emitter.emit(EVENT_CLOSE_MODAL);
  appStore.toggleOverlay(false);
  (appStore.toggleMenu as any)(false);

  appStore.toggleLoading(true);
  await authStore.signOut();
  appStore.toggleLoading(false);
  appStore.navigate('SignIn', 'outer', 'reset');
};

export const updateAppRequired = () => {
  Alert.alert(t('update.updateRequiredTitle'), t('update.updateRequiredMsg'), [
    { text: t('cancel'), style: 'cancel' },
    {
      text: t('update.index'),
      onPress: () => {
        if (IS_IOS) {
          Linking.openURL(APP_STORE_LINK);
        } else {
          Linking.openURL(PLAY_STORE_LINK);
        }
      },
    },
  ]);
};

const onChangeAppState = async (state: string) => {
  const active = state === 'active';

  if (authStore.isLoggedIn) {
    await heroStore.updateStatus(active);

    if (active) {
      heroStore.setActionTime();
    }
  }
};

const LOADING_STATUS_INITIAL = 0;
const LOADING_STATUS_DONE = 2;

(global as any).emitter = new EventEmitter();

if (!IS_WEB) {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: false,
      shouldSetBadge: false,
    }),
  });
}

// TODO: save some time so firebase will do connection
initDb();

const Content = observer(() => {
  const warriorInfoModalOpened = useRef(false);

  return (
    <>
      <View style={{ backgroundColor: BACKGROUND_COLOR, flex: 1 }}>
        <Stack ref={(ref: any) => appStore.setNavigationRef(ref, 'outer')} />
      </View>
      {/* TODO: thing how to refactor this part this.warriorInfoModalOpened */}
      {warriorInfoModalStore.warrior &&
      (IS_WEB || !appStore.modalOpened || warriorInfoModalOpened.current) ? (
        <WarriorInfoModal
          onDismiss={() => {
            warriorInfoModalOpened.current = false;
          }}
          onShow={() => {
            warriorInfoModalOpened.current = true;
          }}
        />
      ) : null}
    </>
  );
});

const App = observer(() => {
  // const update = useRef(false && !__DEV__ && !authStore.isLoggedIn && !IS_WEB);
  const [loadingStatus, setLoadingStatus] = useState(LOADING_STATUS_INITIAL);

  const [fontLoaded] = !IS_WEB
    ? useFonts({
        'Open Sans': require('../../node_modules/npm-font-open-sans/fonts/Regular/OpenSans-Regular.ttf'),
        'Open Sans Bold': require('../../node_modules/npm-font-open-sans/fonts/Bold/OpenSans-Bold.ttf'),
      })
    : [true];

  const init = async () => {
    if (!IS_WEB) {
      NetInfo.addEventListener(onConnectivityChange);

      AppState.addEventListener('change', onChangeAppState);
    }

    const lng =
      (await getFromStorage('lng')) ||
      (['ru', 'es', 'it', 'fr', 'de', 'pt', 'zh', 'ja', 'ko'].includes(
        IS_WEB ? navigator.language : LANGUAGE_CODE,
      )
        ? IS_WEB
          ? navigator.language
          : LANGUAGE_CODE
        : 'en');
    moment.locale(lng);
    await i18next.init({
      lng,
      resources: {
        en: {
          translation: require('../i18n/en.json'),
        },
        ru: {
          translation: require('../i18n/ru.json'),
        },
        es: {
          translation: require('../i18n/es.json'),
        },
        it: {
          translation: require('../i18n/it.json'),
        },
        fr: {
          translation: require('../i18n/fr.json'),
        },
        de: {
          translation: require('../i18n/de.json'),
        },
        pt: {
          translation: require('../i18n/pt.json'),
        },
        zh: {
          translation: require('../i18n/zh.json'),
        },
        ja: {
          translation: require('../i18n/ja.json'),
        },
        ko: {
          translation: require('../i18n/ko.json'),
        },
      },
    });

    setLoadingStatus(LOADING_STATUS_DONE);
  };

  useEffect(() => {
    init();
    return () => {
      if (!IS_WEB) {
        (AppState as any).removeEventListener('change', onChangeAppState);
      }
    };
  }, []);

  useEffect(() => {
    if (loadingStatus === LOADING_STATUS_DONE && fontLoaded) {
      SplashScreen.hideAsync();
    }
  }, [loadingStatus, fontLoaded]);

  if (loadingStatus !== LOADING_STATUS_DONE || !fontLoaded) {
    return null;
  }

  return (
    <View
      style={[
        {
          flex: 1,
          overflow: 'hidden',
          backgroundColor: BACKGROUND_COLOR,
        },
        IS_WEB
          ? {
              minHeight: WINDOW_HEIGHT,
              height: '100vh' as any,
              alignItems: 'center',
            }
          : null,
      ]}
    >
      <StatusBar hidden />
      <Image style={{ position: 'absolute' }} source={require('../assets/images/background.png')} />

      <View
        style={{
          flex: 1,
          paddingVertical: PADDING,
          paddingHorizontal: SAFE_OFFSET + PADDING,
          width: IS_WEB ? 960 : undefined,
        }}
      >
        <Content />
      </View>

      {(appStore.overlay || appStore.modalOpened) && <Overlay loading={appStore.loading} />}
      <Message />
    </View>
  );
});

export default App;
